import { useState } from "react";
import { Plus, Check, ShoppingCart } from "lucide-react";
import { useCart } from "@/lib/CartContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

const SIZE_INFO = {
  small: { label: "Small", serves: "~10" },
  medium: { label: "Medium", serves: "~20" },
  med_large: { label: "Med-Lg", serves: "~25-30" },
  large: { label: "Large", serves: "~35-40" },
};

export default function MenuItem({ item }) {
  const isPerPiece = item.pricing_type === "per_piece";
  const [selectedSize, setSelectedSize] = useState(isPerPiece ? "small" : "small");
  const [justAdded, setJustAdded] = useState(false);
  const { addItem, cart, setCartOpen } = useCart();

  const handleAdd = () => {
    addItem({
      menuItemId: item.id,
      name: item.name,
      size: isPerPiece ? "per_piece" : selectedSize,
      quantity: 1,
      price: item.prices[selectedSize] || item.prices.small,
      isVeg: item.is_veg,
      pricingType: item.pricing_type || "tray",
    });
    setJustAdded(true);
    const priceDisplay = `$${Number(item.prices[selectedSize] || item.prices.small).toFixed(2)}`;
    toast.success(`${item.name} added to cart`, {
      description: isPerPiece ? `Per piece - ${priceDisplay}` : `${SIZE_INFO[selectedSize].label} tray - ${priceDisplay}`,
    });
    setTimeout(() => setJustAdded(false), 1500);
  };

  return (
    <div
      data-testid={`menu-item-${item.id}`}
      className="bg-white rounded-xl border border-stone-100 shadow-[0_2px_12px_rgb(0,0,0,0.03)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.06)] transition-all duration-300 overflow-hidden group"
    >
      <div className="p-5">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span
                data-testid={`veg-indicator-${item.id}`}
                className={`inline-flex items-center justify-center w-4 h-4 rounded-sm border-2 flex-shrink-0 ${
                  item.is_veg
                    ? "border-green-600"
                    : "border-red-600"
                }`}
              >
                <span
                  className={`w-2 h-2 rounded-full ${
                    item.is_veg ? "bg-green-600" : "bg-red-600"
                  }`}
                />
              </span>
              <h3 className="font-heading text-base font-semibold text-[#78350F] leading-tight">
                {item.name}
              </h3>
            </div>
            <p className="font-body text-xs text-stone-400 leading-relaxed line-clamp-2 pl-6">
              {item.description}
            </p>
          </div>
          <span className="font-heading text-lg font-bold text-[#EA580C] whitespace-nowrap">
            ${isPerPiece ? Number(item.prices.small).toFixed(2) : Number(item.prices[selectedSize]).toFixed(2)}
            {isPerPiece && <span className="text-xs font-body font-normal text-stone-400">/pc</span>}
          </span>
        </div>

        {/* Size Selector */}
        {isPerPiece ? (
          <div className="flex items-center gap-2 mb-4 px-1">
            <span className="inline-flex items-center px-3 py-1.5 rounded-lg bg-amber-50 text-xs font-body font-medium text-amber-700">
              Priced per piece
            </span>
          </div>
        ) : (
          <div className="flex items-center gap-1 mb-4">
            {Object.entries(SIZE_INFO).map(([key, info]) => (
              <button
                key={key}
                data-testid={`size-${key}-${item.id}`}
                onClick={() => setSelectedSize(key)}
                className={`flex-1 py-1.5 px-1 rounded-lg text-xs font-body font-medium transition-all ${
                  selectedSize === key
                    ? "bg-[#EA580C] text-white shadow-sm"
                    : "bg-stone-50 text-stone-500 hover:bg-stone-100"
                }`}
              >
                <span className="block">{info.label}</span>
                <span className="block text-[10px] opacity-70">
                  {info.serves}
                </span>
              </button>
            ))}
          </div>
        )}

        {/* Add to Cart */}
        <Button
          data-testid={`add-to-cart-${item.id}`}
          onClick={handleAdd}
          className={`w-full rounded-full font-body font-semibold h-9 text-sm transition-all active:scale-95 ${
            justAdded
              ? "bg-green-600 hover:bg-green-600 text-white"
              : "bg-[#EA580C] hover:bg-[#C2410C] text-white shadow-sm hover:shadow-md"
          }`}
        >
          {justAdded ? (
            <>
              <Check className="h-4 w-4 mr-1" /> Added
            </>
          ) : (
            <>
              <Plus className="h-4 w-4 mr-1" /> Add to Cart
            </>
          )}
        </Button>
        {cart.length > 0 && (
          <Button
            data-testid={`go-to-cart-${item.id}`}
            onClick={() => setCartOpen(true)}
            variant="outline"
            className="w-full rounded-full font-body font-semibold h-9 text-sm mt-2 border-[#EA580C] text-[#EA580C] hover:bg-[#FFF7ED] transition-all active:scale-95"
          >
            <ShoppingCart className="h-4 w-4 mr-1" /> Go to Cart ({cart.length})
          </Button>
        )}
      </div>
    </div>
  );
}
